<template>
    <div
        class="multiple-content-cell d-flex align-items-center"
        :class="{hoverable: hoverable}"
    >
        <b-check
            v-if="!isLoading && hasMultiSelect"
            class="ms-multiselect ms-multiselect-checkbox ms-multiselect-checkbox-item"
            :value="appResource"
            :unchecked-value="appResource"
            @click.native.stop
        />

        <div
            v-if="isLoading"
            class="avatar-wrapper mr-2"
        >
            <image-loading-placeholder
                :height="imageHeight? imageHeight : '2.5rem'"
                :width="imageWidth ? imageWidth : '2.5rem'"
                :circle="(avatar || textAvatar || icon(appResource).name) ? true : this.imageCircle"
                :rounded="(avatar || textAvatar || icon(appResource).name) ? true : this.imageRound"
                class="avatar d-block"
            />
        </div>

        <template v-else>
            <template v-if="avatar || textAvatar">
                <avatar
                    v-if="(avatar && !isDefaultAvatar) || (isDefaultAvatar && !textAvatar)"
                    :src="avatarUrl"
                    :size="2.5"
                    class="mr-2"
                    @click.native.stop="avatarAction(appResource)"
                />

                <avatar
                    v-else-if="textAvatar"
                    :name="avatarName"
                    :size="2.5"
                    class="mr-2"
                    @click.native.stop="avatarAction(appResource)"
                />
            </template>

            <cell-dynamic-avatar
                v-else-if="hasAvatar && dynamicAvatar"
                :app-resource="appResource"
                :callback="dynamicAvatarCallback"
                @click.native.stop="avatarAction(appResource)"
            />

            <b-img
                v-else-if="image && image(appResource)"
                :src="image(appResource)"
                :width="this.imageWidth"
                :height="this.imageHeight"
                fluid
                class="cell-image mr-2"
                @click.stop="avatarAction(appResource)"
            />
            <i
                v-if="icon(appResource).name"
                :class="getIconStyle(icon(appResource))"
                class="cell-icon text-gray-700"
            />
        </template>

        <i
            v-if="!isLoading && (getNestedValue(lockedFieldName) || isLocked(appResource, meta))"
            class="fa fa-lock text-gray-600 mr-2"
        />
        <div
            class="d-inline-block"
            :style="{
                    width: !isLoading ? null : 'calc(100% - 3em)'
                }"
        >
            <text-loading-placeholder
                v-if="isLoading || loadingPrimaryContent"
                font-size="1rem"
                class="title d-block w-75"
            />
            <template v-else>
                <div class="d-flex align-items-center">
                    <div class="initial-title">
                        <router-link
                            v-if="Object.keys(castPrimaryContentLink()).length"
                            :to="castPrimaryContentLink()"
                            class="title text-decoration-none"
                            :class="primaryClasses"
                            :title="computedPrimaryContent"
                            :target="castPrimaryContentLink().target"
                            @click.native.stop
                        >
                            <v-clamp
                                :max-lines="2"
                                autoresize
                            >
                                {{ computedPrimaryContent }}
                            </v-clamp>
                        </router-link>
                        <div
                            v-else
                            class="title"
                            :class="primaryClasses"
                            :title="computedPrimaryContent"
                        >
                            {{ computedPrimaryContent }}
                        </div>
                    </div>

                    <b-badge
                        v-if="displayBadge"
                        class="primary-content-badge ml-2"
                        :class="badgeClasses"
                    >
                        {{ badge.text }}
                    </b-badge>
                </div>
            </template>
            <template v-if="bottomContent">
                <text-loading-placeholder
                    v-if="isLoading || loadingSecondaryContent"
                    font-size="0.875rem"
                    class="subtitle d-block mt-1 w-50"
                    :style="{
                            minWidth: '50%'
                        }"
                />
                <div
                    class="subtitle"
                    :class="secondaryContentClasses"
                    v-else
                    :title="getNestedValue(secondaryContent)"
                >
                    {{ bottomContent }}
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import './MultipleContentCell.scss';
import '../../ResourceListingTable.scss';
import TableCellsMixin from "../../Mixins/TableCellsMixin";
import {AppResource} from  '@sellers-shield-home-protection/ss-core-js';
import CellDynamicAvatar from './CellDynamicAvatar/CellDynamicAvatar';
import isEmpty from 'lodash/isEmpty';
import VClamp from 'vue-clamp';

export default {
    name: 'MultipleContentCell',
    components: {
        CellDynamicAvatar,
        VClamp
    },

    mixins: [TableCellsMixin],

    props: {
        avatar: {
            type: String,
            default: '',
        },
        textAvatar: {
            type: String,
            default: '',
        },
        lockedFieldName: {
            type: String,
            default: 'locked',
        },
        avatarAction: {
            type: Function,
            default: (option) => {
            },
        },
        primaryContent: {
            type: [String, Object],
            required: true,
            default: '',
        },
        primaryContentLink: {
            type: Object,
            default: () => {
            },
        },
        determineSecondaryContent: {
            type: Boolean,
            default: false,
        },
        determineSecondaryContentCallback: {
            type: Function,
            default: () => {
            },
        },
        secondaryContent: {
            type: String,
            default: '',
        },
        tertiaryContent: {
            type: String,
            default: '',
        },
        bottomContentSeparator: {
            type: String,
            default: ' ',
        },
        appResource: {
            type: AppResource,
            required: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
            required: false,
        },
        image: {
            type: Function,
            default: () => {
                return null;
            }
        },
        imageWidth: {
            type: [String, Number],
            default: () => {
                return null;
            }
        },
        imageHeight: {
            type: [String, Number],
            default: () => {
                return null;
            }
        },
        imageCircle: {
            type: Boolean,
            default: false
        },
        imageRound: {
            type: Boolean,
            default: false
        },
        badge: {
            type: Object,
            default: () => {
                return {};
            },
        },

        primaryContentClasses: {
            type: Array,
            default: () => [],
        },
        isLocked: {
            type: Function,
            default: () => false,
        },
        meta: {
            type: Object,
            default: () => {
            },
        },
        icon: {
            type: Function,
            default: () => {
                return {
                    style: 'light',
                    name: ''
                };
            }
        },
        hoverable: {
            type: Boolean,
            default: false,
        },
        determinePrimaryContent: {
            type: Boolean,
            default: false,
        },
        determinePrimaryContentCallback: {
            type: Function,
            default: () => {
            },
        },
        secondaryContentClasses: {
            type: Array,
            default: () => [],
        },
        hasAvatar: {
            type: Boolean,
            default: true,
        },
        dynamicAvatar: {
            type: Boolean,
            default: false,
        },
        dynamicAvatarCallback: {
            type: Function,
            default: () => {
            },
        },
        hasMultiSelect: {
            type: Boolean,
            default: false,
        },
        hidePrimaryLinkCallback: {
            type: Function,
            default: () => {
                return false;
            }
        },
        primaryContentClassesCallback: {
            type: Function,
            default: () => []
        },
    },
    data() {
        return {
            loadingPrimaryContent: this.determinePrimaryContent,
            loadingSecondaryContent: this.determineSecondaryContent,
            computedPrimaryContent: '',
            bottomContent: '',
        };
    },

    computed: {
        isDefaultAvatar() {
            return isEmpty(this.avatar) || this.getNestedValue(this.avatar);
        },
        avatarUrl() {
            return !isEmpty(this.avatar) && this.getNestedValue(this.avatar) ?
                this.getNestedValue(this.avatar).publicUrl :
                require('./../../../../static/defaults/default-user-avatar.png');
        },
        avatarName() {
            return !isEmpty(this.textAvatar) && this.getNestedValue(this.textAvatar) ?
                this.getNestedValue(this.textAvatar) :
                'John Doe';
        },

        displayBadge() {
            return !isEmpty(this.badge) && this.badge.validator(this.appResource);
        },

        badgeClasses() {
            let classes = [];

            if (this.badge.bgColor) {
                classes.push('bg-' + this.badge.bgColor);
            }

            if (this.badge.textColor) {
                classes.push('text-' + this.badge.textColor);
            }

            return classes;
        },

        primaryClasses() {
            return this.primaryContentClasses.concat(this.primaryContentClassesCallback(this.appResource));
        }
    },

    methods: {
        async setPrimaryContent() {
            if (!this.appResource) {
                return;
            }

            if (this.determinePrimaryContent) {
                this.loadingPrimaryContent = true;
                let content = await this.determinePrimaryContentCallback(this.appResource);
                this.loadingPrimaryContent = false;

                this.computedPrimaryContent = content;
                return;
            }

            if (typeof this.primaryContent === "object") {
                let content = '';
                Object.keys(this.primaryContent).forEach((key) => {
                    content += ' ' + this.getNestedValue(this.primaryContent[key]);
                });
                this.computedPrimaryContent = content;
                return;
            }

            this.computedPrimaryContent = this.getNestedValue(this.primaryContent);
        },

        async setBottomContent() {
            if (!this.appResource) {
                return;
            }

            if (this.determineSecondaryContent) {
                this.loadingSecondaryContent = true;
                let content = await this.determineSecondaryContentCallback(this.appResource);
                this.loadingSecondaryContent = false;

                this.bottomContent = content;
            } else {
                let secondaryContent = this.getNestedValue(this.secondaryContent);
                let tertiaryContent = this.getNestedValue(this.tertiaryContent);

                this.bottomContent = [secondaryContent, tertiaryContent].join(this.bottomContentSeparator);
            }
        },

        getIconStyle(icon) {
            let style = 'fal';
            switch (icon.style) {
                case 'solid':
                    style = 'fas';
                    break;
                case 'regular':
                    style = 'far';
                    break;
                case 'light':
                    style = 'fal';
                    break;
                default:
                    style = 'fal';
            }
            return [style, `fa-${icon.name}`];
        },

        castPrimaryContentLink() {
            if (!this.appResource || this.hidePrimaryLinkCallback(this.appResource)) {
                return {};
            }

            return this.setPrimaryContentLink();
        },


        setPrimaryContentLink() {
            let route = {};
            if (this.primaryContentLink && Object.keys(this.primaryContentLink).length) {
                if (this.primaryContentLink.hasOwnProperty('name')) {
                    route.name = this.primaryContentLink.name;
                }
                if (this.primaryContentLink.hasOwnProperty('path')) {
                    route.path = this.primaryContentLink.path;
                }
                if (this.primaryContentLink.hasOwnProperty('params')) {
                    route.params = {};
                    for (let param in this.primaryContentLink.params) {
                        const value = this.primaryContentLink.params[param];
                        route.params[param] = this.getNestedValue(value);

                    }
                }

                if (this.primaryContentLink.hasOwnProperty('query')) {
                    route.query = {};
                    for (let param in this.primaryContentLink.query) {
                        const value = this.primaryContentLink.query[param];
                        route.query[param] = this.getNestedValue(value);
                    }
                }

                if (this.primaryContentLink.hasOwnProperty('target')) {
                    route.target = this.primaryContentLink.target;
                }

            }

            return route;
        },
    },

    async mounted() {
        await this.setPrimaryContent();
        await this.setBottomContent();
    }
};
</script>
